import React from "react";

import { INextGenDataSourceStatus } from "../../services/zeos-api/contracts/Dashboard";
import { MidFiChip } from "./MidFiChip";

interface IProps {
  sources?: INextGenDataSourceStatus[];
  /** Overall data / verification state shown at the end of the strip, e.g. "Priority data gap". */
  badge?: string | null;
  /** Standing context chip, e.g. "EVSE = added load, own P&L". */
  note?: string | null;
}

const STATUS_DISPLAY: Record<string, { label: string; led: string }> = {
  ok: { label: "Connected", led: "bg-mf-energy" },
  partial: { label: "Partial", led: "bg-mf-orange" },
  not_connected: { label: "Not connected", led: "border-2 border-mf-faint bg-white" },
  error: { label: "Error", led: "bg-mf-alert" },
};

// Data-status strip (4019), mid-fi styling (UX-05): an LED per feed (green connected, orange
// partial / estimate, hollow not connected), then the strip's overall badge and any context chip.
export const DataStatusStrip = ({ sources, badge, note }: IProps) => {
  if (!sources?.length) {
    return null;
  }

  return (
    <div
      className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-xl border border-mf-border bg-mf-card px-[18px] py-[11px]"
      aria-label="Data status"
    >
      {sources.map((source) => {
        const display = STATUS_DISPLAY[source.status] ?? {
          label: source.status,
          led: "bg-mf-faint",
        };
        return (
          <div key={source.source} className="flex items-center gap-2 text-xs font-semibold text-mf-sub">
            <span className={`h-[9px] w-[9px] flex-none rounded-full ${display.led}`} aria-hidden="true" />
            <span className="font-bold text-mf-ink">{source.label}</span>
            <span>{display.label}</span>
            {source.message ? (
              <>
                <span aria-hidden="true">—</span>
                <span>{source.message}</span>
              </>
            ) : null}
          </div>
        );
      })}
      {note || badge ? (
        <div className="flex flex-wrap gap-2 lg:ml-auto">
          {note ? <MidFiChip label={note} tone="info" /> : null}
          {badge ? <MidFiChip label={badge} tone="warn" /> : null}
        </div>
      ) : null}
    </div>
  );
};
